import { useState, useEffect, useMemo } from "react";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import { useDispatch } from "react-redux";
import { createTaxRule, updateTaxRule } from "@/api/taxRules.js";
import { getPlans as getCategories } from "@/api/marketplace.js";

const emptyRule = {
  name: '',
  description: '',
  is_alcohol: false,
  is_tobacco: false,
  flat_rate: 0,
  rate: 0,
  rate_type: 'percentage',
  rate_unit: 'per_item',
  tax_codes: [],
  categories: [],
  default_categories: []
};

const emptyFormData = {
  name: '',
  description: '',
  active: true,
  statewide: false,
  state_codes: [],
  zip_codes: [],
  cities: [],
  line_item_display: false,
  line_item_text: '',
  line_item_info: '',
  customer_receipt_display: false,
  customer_receipt_text: '',
  vendor_receipt_display: false,
  vendor_receipt_text: '',
  rules: []
};

function TaxRuleModal({ isOpen, setIsOpen, rule, onSave }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ ...emptyFormData });
  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [activeCategoryPicker, setActiveCategoryPicker] = useState(null); // { ruleIndex, field }
  const isEditMode = Boolean(rule?._id);

  // Fetch categories if any rule has categories or default_categories
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Initialize form when rule changes
  useEffect(() => {
    setCategorySearch('');
    setActiveCategoryPicker(null);
    if (rule) {
      setFormData({
        name: rule.name || '',
        description: rule.description || '',
        active: rule.active !== undefined ? rule.active : true,
        statewide: rule.statewide || false,
        state_codes: rule.state_codes || [],
        zip_codes: rule.zip_codes || [],
        cities: rule.cities || [],
        line_item_display: rule.line_item_display || false,
        line_item_text: rule.line_item_text || '',
        line_item_info: rule.line_item_info || '',
        customer_receipt_display: rule.customer_receipt_display || false,
        customer_receipt_text: rule.customer_receipt_text || '',
        vendor_receipt_display: rule.vendor_receipt_display || false,
        vendor_receipt_text: rule.vendor_receipt_text || '',
        rules: rule.rules || []
      });
    } else {
      setFormData({ ...emptyFormData, rules: [] });
    }
  }, [rule, isOpen]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle comma-separated array fields (state_codes, zip_codes, cities)
  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value.split(',').map(v => v.trim()).filter(Boolean)
    }));
  };

  // Sub-rules management
  const addSubRule = () => {
    setFormData(prev => ({
      ...prev,
      rules: [...prev.rules, { ...emptyRule }]
    }));
  };

  const removeSubRule = (index) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const handleSubRuleChange = (index, field, value) => {
    const newRules = [...formData.rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  const handleSubRuleCheckbox = (index, field) => {
    const newRules = [...formData.rules];
    newRules[index] = { ...newRules[index], [field]: !newRules[index][field] };
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  const addTaxCode = (index, code) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    const newRules = [...formData.rules];
    const existing = newRules[index].tax_codes || [];
    if (existing.includes(trimmed)) return;
    newRules[index] = { ...newRules[index], tax_codes: [...existing, trimmed] };
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  const removeTaxCode = (index, code) => {
    const newRules = [...formData.rules];
    newRules[index] = { ...newRules[index], tax_codes: (newRules[index].tax_codes || []).filter(c => c !== code) };
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  // Category picker
  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    if (!categorySearch.trim()) return categories;
    const q = categorySearch.toLowerCase();
    return categories.filter(c =>
      c.name?.toLowerCase().includes(q) || c.tag?.toLowerCase().includes(q)
    );
  }, [categories, categorySearch]);

  const toggleCategory = (ruleIndex, field, categoryId) => {
    const id = getCatId(categoryId);
    const newRules = [...formData.rules];
    const current = newRules[ruleIndex][field] || [];
    const currentIds = current.map(getCatId);
    if (currentIds.includes(id)) {
      newRules[ruleIndex] = { ...newRules[ruleIndex], [field]: current.filter(c => getCatId(c) !== id) };
    } else {
      newRules[ruleIndex] = { ...newRules[ruleIndex], [field]: [...current, id] };
    }
    setFormData(prev => ({ ...prev, rules: newRules }));
  };

  // Categories can be either ObjectId strings or populated objects from the API
  const getCatId = (catOrId) => (typeof catOrId === 'object' && catOrId?._id) ? catOrId._id : catOrId;
  const getCategoryName = (catOrId) => {
    if (typeof catOrId === 'object' && catOrId?.name) return catOrId.name;
    if (!Array.isArray(categories)) return catOrId;
    const cat = categories.find(c => c._id === catOrId);
    return cat ? cat.name : catOrId;
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      dispatch(addToast({ message: 'Rule name is required', type: 'error' }));
      return false;
    }
    if (formData.rules.length === 0) {
      dispatch(addToast({ message: 'At least one sub-rule is required', type: 'error' }));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEditMode) {
        await updateTaxRule(rule._id, formData);
        dispatch(addToast({ message: 'Tax rule updated successfully', type: 'success' }));
      } else {
        await createTaxRule(formData);
        dispatch(addToast({ message: 'Tax rule created successfully', type: 'success' }));
      }
      onSave?.();
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving tax rule:', error);
      dispatch(addToast({
        message: error.response?.data?.message || 'Failed to save tax rule',
        type: 'error'
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={isEditMode ? 'Edit Tax Rule' : 'Create Tax Rule'}
      width="4xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Name & Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="name">
                Name <span className="text-rose-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                className="form-input w-full"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., California Alcohol Tax"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="active-toggle">
                Status
              </label>
              <div className="flex items-center mt-2">
                <input
                  id="active-toggle"
                  name="active"
                  type="checkbox"
                  className="form-checkbox"
                  checked={formData.active}
                  onChange={handleChange}
                />
                <label htmlFor="active-toggle" className="ml-2 text-sm font-medium">
                  Active
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-textarea w-full"
              rows="2"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description"
            />
          </div>

          {/* Geographic Conditions */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
              Geographic Conditions
            </h3>

            <div className="flex items-center mb-3">
              <input
                id="statewide"
                name="statewide"
                type="checkbox"
                className="form-checkbox"
                checked={formData.statewide}
                onChange={handleChange}
              />
              <label htmlFor="statewide" className="ml-2 text-sm font-medium">
                Statewide
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  State Codes
                </label>
                <input
                  className="form-input w-full"
                  type="text"
                  value={formData.state_codes?.join(', ') || ''}
                  onChange={(e) => handleArrayChange('state_codes', e.target.value)}
                  placeholder="CA, NY, TX"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Zip Codes
                </label>
                <input
                  className="form-input w-full"
                  type="text"
                  value={formData.zip_codes?.join(', ') || ''}
                  onChange={(e) => handleArrayChange('zip_codes', e.target.value)}
                  placeholder="90210, 10001"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Cities
                </label>
                <input
                  className="form-input w-full"
                  type="text"
                  value={formData.cities?.join(', ') || ''}
                  onChange={(e) => handleArrayChange('cities', e.target.value)}
                  placeholder="Los Angeles, New York"
                />
                <p className="text-xs text-slate-500 mt-1">Comma-separated</p>
              </div>
            </div>
          </div>

          {/* Display Options */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
              Display Options
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Line Item */}
              <div>
                <div className="flex items-center mb-2">
                  <input
                    id="line_item_display"
                    name="line_item_display"
                    type="checkbox"
                    className="form-checkbox"
                    checked={formData.line_item_display}
                    onChange={handleChange}
                  />
                  <label htmlFor="line_item_display" className="ml-2 text-sm font-medium">
                    Line Item Display
                  </label>
                </div>
                {formData.line_item_display && (
                  <>
                    <input
                      name="line_item_text"
                      className="form-input w-full mb-2"
                      type="text"
                      value={formData.line_item_text}
                      onChange={handleChange}
                      placeholder="Line item text"
                    />
                    <input
                      name="line_item_info"
                      className="form-input w-full"
                      type="text"
                      value={formData.line_item_info}
                      onChange={handleChange}
                      placeholder="Line item info"
                    />
                  </>
                )}
              </div>

              {/* Customer Receipt */}
              <div>
                <div className="flex items-center mb-2">
                  <input
                    id="customer_receipt_display"
                    name="customer_receipt_display"
                    type="checkbox"
                    className="form-checkbox"
                    checked={formData.customer_receipt_display}
                    onChange={handleChange}
                  />
                  <label htmlFor="customer_receipt_display" className="ml-2 text-sm font-medium">
                    Customer Receipt
                  </label>
                </div>
                {formData.customer_receipt_display && (
                  <input
                    name="customer_receipt_text"
                    className="form-input w-full"
                    type="text"
                    value={formData.customer_receipt_text}
                    onChange={handleChange}
                    placeholder="Customer receipt text"
                  />
                )}
              </div>

              {/* Vendor Receipt */}
              <div>
                <div className="flex items-center mb-2">
                  <input
                    id="vendor_receipt_display"
                    name="vendor_receipt_display"
                    type="checkbox"
                    className="form-checkbox"
                    checked={formData.vendor_receipt_display}
                    onChange={handleChange}
                  />
                  <label htmlFor="vendor_receipt_display" className="ml-2 text-sm font-medium">
                    Vendor Receipt
                  </label>
                </div>
                {formData.vendor_receipt_display && (
                  <input
                    name="vendor_receipt_text"
                    className="form-input w-full"
                    type="text"
                    value={formData.vendor_receipt_text}
                    onChange={handleChange}
                    placeholder="Vendor receipt text"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Sub-Rules */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Tax Rules <span className="text-rose-500">*</span>
              </h3>
              <button
                type="button"
                onClick={addSubRule}
                className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                + Add Rule
              </button>
            </div>

            {formData.rules.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No rules added yet. Click "+ Add Rule" to create one.
              </p>
            ) : (
              <div className="space-y-4">
                {formData.rules.map((subRule, index) => (
                  <div
                    key={index}
                    className="bg-slate-50 dark:bg-slate-800/50 rounded p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Rule {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSubRule(index)}
                        className="text-rose-500 hover:text-rose-600 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Name & Description */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Name</label>
                        <input
                          type="text"
                          className="form-input w-full text-sm"
                          value={subRule.name || ''}
                          onChange={(e) => handleSubRuleChange(index, 'name', e.target.value)}
                          placeholder="Rule name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Description</label>
                        <input
                          type="text"
                          className="form-input w-full text-sm"
                          value={subRule.description || ''}
                          onChange={(e) => handleSubRuleChange(index, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </div>
                    </div>

                    {/* Rate Settings */}
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Rate Type</label>
                        <select
                          className="form-select w-full text-sm"
                          value={subRule.rate_type || 'percentage'}
                          onChange={(e) => handleSubRuleChange(index, 'rate_type', e.target.value)}
                        >
                          <option value="percentage">Percentage</option>
                          <option value="flat">Flat</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">
                          Rate {subRule.rate_type === 'percentage' ? '(%)' : '($)'}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-input w-full text-sm"
                          value={subRule.rate_type === 'percentage' ? (subRule.rate ? subRule.rate * 100 : '') : (subRule.rate || '')}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            handleSubRuleChange(index, 'rate', subRule.rate_type === 'percentage' ? val / 100 : val);
                          }}
                          placeholder={subRule.rate_type === 'percentage' ? '5' : '0'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Flat Rate ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-input w-full text-sm"
                          value={subRule.flat_rate || ''}
                          onChange={(e) => handleSubRuleChange(index, 'flat_rate', Number(e.target.value))}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Rate Unit</label>
                        <select
                          className="form-select w-full text-sm"
                          value={subRule.rate_unit || 'per_item'}
                          onChange={(e) => handleSubRuleChange(index, 'rate_unit', e.target.value)}
                        >
                          <option value="per_item">Per Item</option>
                          <option value="per_unit">Per Unit</option>
                          <option value="per_liter">Per Liter</option>
                        </select>
                      </div>
                    </div>

                    {/* Flags */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={subRule.is_alcohol || false}
                          onChange={() => handleSubRuleCheckbox(index, 'is_alcohol')}
                          id={`alcohol-${index}`}
                        />
                        <label htmlFor={`alcohol-${index}`} className="ml-2 text-sm">Alcohol</label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          checked={subRule.is_tobacco || false}
                          onChange={() => handleSubRuleCheckbox(index, 'is_tobacco')}
                          id={`tobacco-${index}`}
                        />
                        <label htmlFor={`tobacco-${index}`} className="ml-2 text-sm">Tobacco</label>
                      </div>
                    </div>

                    {/* Tax Codes */}
                    <div>
                      <label className="block text-xs font-medium mb-1">Tax Codes</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="form-input w-full text-sm"
                          placeholder="Enter a tax code"
                          id={`tax-code-input-${index}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addTaxCode(index, e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.getElementById(`tax-code-input-${index}`);
                            addTaxCode(index, input.value);
                            input.value = '';
                          }}
                          className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white whitespace-nowrap"
                        >
                          Add
                        </button>
                      </div>
                      {subRule.tax_codes?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {subRule.tax_codes.map((code, codeIdx) => (
                            <span
                              key={`${codeIdx}-${code}`}
                              className="inline-flex items-center bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs px-2 py-1 rounded"
                            >
                              {code}
                              <button
                                type="button"
                                onClick={() => removeTaxCode(index, code)}
                                className="ml-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Categories */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">
                          Categories ({subRule.categories?.length || 0})
                        </label>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {subRule.categories?.map((catOrId, catIdx) => (
                            <span
                              key={getCatId(catOrId) || catIdx}
                              className="inline-flex items-center bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs px-2 py-1 rounded"
                            >
                              {getCategoryName(catOrId)}
                              <button
                                type="button"
                                onClick={() => toggleCategory(index, 'categories', catOrId)}
                                className="ml-1 text-indigo-500 hover:text-indigo-700"
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveCategoryPicker(
                            activeCategoryPicker?.ruleIndex === index && activeCategoryPicker?.field === 'categories'
                              ? null
                              : { ruleIndex: index, field: 'categories' }
                          )}
                          className="text-xs text-indigo-500 hover:text-indigo-600"
                        >
                          {activeCategoryPicker?.ruleIndex === index && activeCategoryPicker?.field === 'categories'
                            ? 'Close' : 'Select Categories'}
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">
                          Default Categories ({subRule.default_categories?.length || 0})
                        </label>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {subRule.default_categories?.map((catOrId, catIdx) => (
                            <span
                              key={getCatId(catOrId) || catIdx}
                              className="inline-flex items-center bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs px-2 py-1 rounded"
                            >
                              {getCategoryName(catOrId)}
                              <button
                                type="button"
                                onClick={() => toggleCategory(index, 'default_categories', catOrId)}
                                className="ml-1 text-amber-500 hover:text-amber-700"
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveCategoryPicker(
                            activeCategoryPicker?.ruleIndex === index && activeCategoryPicker?.field === 'default_categories'
                              ? null
                              : { ruleIndex: index, field: 'default_categories' }
                          )}
                          className="text-xs text-indigo-500 hover:text-indigo-600"
                        >
                          {activeCategoryPicker?.ruleIndex === index && activeCategoryPicker?.field === 'default_categories'
                            ? 'Close' : 'Select Default Categories'}
                        </button>
                      </div>
                    </div>

                    {/* Category Picker Dropdown */}
                    {activeCategoryPicker?.ruleIndex === index && (
                      <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
                        <input
                          type="text"
                          placeholder="Search categories by name or tag..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="form-input w-full text-sm mb-2"
                        />
                        <div className="max-h-40 overflow-y-auto">
                          {filteredCategories.length === 0 ? (
                            <p className="text-xs text-slate-500 text-center py-2">No categories found</p>
                          ) : (
                            filteredCategories.map(cat => {
                              const isSelected = (subRule[activeCategoryPicker.field] || []).map(getCatId).includes(cat._id);
                              return (
                                <div
                                  key={cat._id}
                                  onClick={() => toggleCategory(index, activeCategoryPicker.field, cat._id)}
                                  className={`p-2 text-sm cursor-pointer rounded hover:bg-slate-100 dark:hover:bg-slate-700 flex justify-between items-center ${
                                    isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                                  }`}
                                >
                                  <span>{cat.name} {cat.tag && <span className="text-xs text-slate-400">({cat.tag})</span>}</span>
                                  {isSelected && <span className="text-indigo-500 text-xs font-medium">Selected</span>}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end space-x-2 mt-6">
          <SecondaryButton
            type="button"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={loading}
          >
            {isEditMode ? 'Update Rule' : 'Create Rule'}
          </PrimaryButton>
        </div>
      </form>
    </ModalBasic>
  );
}

export default TaxRuleModal;
